import React, { forwardRef, useMemo, useState, useCallback } from 'react';
import {
  TextField,
  TextFieldProps,
  Autocomplete,
  AutocompleteProps,
  InputAdornment,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Popper,
  Paper,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';

// Enhanced option interface
export interface SAPInputOption {
  label: string;
  value: string | number;
  code?: string;
  status?: number;
  disabled?: boolean;
  group?: string;
  description?: string;
  icon?: React.ReactNode;
  metadata?: Record<string, any>;
}

// Component variants using CVA for type-safe styling
const inputVariants = cva(
  // Base styles
  'transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        outlined: 'border border-gray-300 rounded-md',
        filled: 'bg-gray-50 border-b-2 border-gray-300',
        standard: 'border-b-2 border-gray-300',
      },
      size: {
        xs: 'h-6 text-xs px-2 py-1',
        sm: 'h-8 text-sm px-3 py-1.5',
        md: 'h-10 text-base px-4 py-2',
        lg: 'h-12 text-lg px-4 py-3',
        xl: 'h-14 text-xl px-5 py-4',
      },
      state: {
        default: '',
        error: 'border-red-500 focus:border-red-500',
        success: 'border-green-500 focus:border-green-500',
        warning: 'border-yellow-500 focus:border-yellow-500',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
    },
    defaultVariants: {
      variant: 'outlined',
      size: 'md',
      state: 'default',
      fullWidth: true,
    },
  }
);

// Enhanced TypeScript interface with all possible props
export interface SAPInputProps extends Omit<TextFieldProps, 'variant' | 'size'> {
  // Custom props
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  state?: 'default' | 'error' | 'success' | 'warning';
  fullWidth?: boolean;
  
  // Enhanced accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
  
  // Loading state
  loading?: boolean;
  loadingText?: string;
  
  // Helper text with different types
  helperText?: string;
  helperTextType?: 'info' | 'error' | 'success' | 'warning';
  
  // Icon support
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  
  // Character count
  showCharacterCount?: boolean;
  maxLength?: number;
  
  // Validation
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  
  // Custom styling
  customStyles?: React.CSSProperties;
  
  // Callbacks
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  onStateChange?: (state: 'default' | 'error' | 'success' | 'warning') => void;
  
  // Autocomplete specific props
  type?: 'text' | 'autocomplete' | 'select';
  options?: SAPInputOption[];
  multiple?: boolean;
  freeSolo?: boolean;
  clearable?: boolean;
  groupBy?: (option: SAPInputOption) => string;
  groupLabelRenderer?: (group: string) => React.ReactNode;
  filterOptions?: (options: SAPInputOption[], params: any) => SAPInputOption[];
  highlightMatches?: boolean;
  showOptionDescription?: boolean;
  showOptionIcon?: boolean;
  showOptionCode?: boolean;
  showOptionStatus?: boolean;
  onOptionSelect?: (option: SAPInputOption | SAPInputOption[]) => void;
  onInputChange?: (value: string) => void;
  noOptionsText?: string;
  optionHeight?: number;
  maxOptions?: number;
}

// Styled components with SAP design system
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => 
    !['state', 'loading', 'helperTextType', 'showCharacterCount', 'customStyles'].includes(prop as string),
})<SAPInputProps>(({ theme, state = 'default', loading, helperTextType, showCharacterCount, customStyles }) => ({
  // Base styles
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: SAP_SPACING.component.borderRadius,
    transition: SAP_TRANSITIONS.normal,
    border: `1px solid ${getBorderColor(state)}`,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    
    // Hover state
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
      borderColor: getHoverBorderColor(state),
      transform: 'translateY(-1px)',
      boxShadow: SAP_SHADOWS.sm,
    },
    
    // Focus state
    '&.Mui-focused': {
      backgroundColor: SAP_COLORS.sapWhite,
      borderColor: getFocusBorderColor(state),
      boxShadow: `0 0 0 2px ${getFocusShadowColor(state)}`,
      transform: 'translateY(-1px)',
    },
    
    // Disabled state
    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      borderColor: SAP_COLORS.sapGray3,
      color: SAP_COLORS.sapGray4,
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
    
    // Error state
    '&.Mui-error': {
      borderColor: SAP_COLORS.sapRed,
      '&:hover': {
        borderColor: SAP_COLORS.sapRed,
      },
      '&.Mui-focused': {
        borderColor: SAP_COLORS.sapRed,
        boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightRed}`,
      },
    },
  },
  
  // Input styles
  '& .MuiOutlinedInput-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    padding: SAP_SPACING.component.padding,
    lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
    
    '&::placeholder': {
      color: SAP_COLORS.sapGray4,
      opacity: 1,
      fontStyle: 'italic',
    },
    
    // Loading state
    ...(loading && {
      color: SAP_COLORS.sapGray4,
      cursor: 'wait',
    }),
  },
  
  // Label styles
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    
    '&.Mui-focused': {
      color: getFocusLabelColor(state),
      fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    },
    
    '&.Mui-error': {
      color: SAP_COLORS.sapRed,
    },
  },
  
  // Helper text styles
  '& .MuiFormHelperText-root': {
    margin: 0,
    marginTop: SAP_SPACING.xs,
    fontSize: SAP_TYPOGRAPHY.fontSize.xs,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
    
    ...(helperTextType === 'error' && {
      color: SAP_COLORS.sapRed,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    }),
    
    ...(helperTextType === 'success' && {
      color: SAP_COLORS.sapGreen,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    }),
    
    ...(helperTextType === 'warning' && {
      color: SAP_COLORS.sapOrange,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    }),
    
    ...(helperTextType === 'info' && {
      color: SAP_COLORS.sapGray5,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    }),
  },
  
  // Character count styles
  ...(showCharacterCount && {
    '& .MuiInputBase-input': {
      paddingRight: '4rem',
    },
  }),
  
  // Custom styles override
  ...customStyles,
}));

// Styled Autocomplete component
const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: (prop) => 
    !['state', 'loading', 'helperTextType', 'customStyles', 'optionHeight', 'size'].includes(prop as string),
})<SAPInputProps>(({ theme, state = 'default', loading, helperTextType, customStyles, optionHeight = 48 }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: SAP_SPACING.component.borderRadius,
    transition: SAP_TRANSITIONS.normal,
    border: `1px solid ${getBorderColor(state)}`,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
      borderColor: getHoverBorderColor(state),
      transform: 'translateY(-1px)',
      boxShadow: SAP_SHADOWS.sm,
    },
    
    '&.Mui-focused': {
      backgroundColor: SAP_COLORS.sapWhite,
      borderColor: getFocusBorderColor(state),
      boxShadow: `0 0 0 2px ${getFocusShadowColor(state)}`,
      transform: 'translateY(-1px)',
    },
    
    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      borderColor: SAP_COLORS.sapGray3,
      color: SAP_COLORS.sapGray4,
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
    
    '&.Mui-error': {
      borderColor: SAP_COLORS.sapRed,
      '&:hover': {
        borderColor: SAP_COLORS.sapRed,
      },
      '&.Mui-focused': {
        borderColor: SAP_COLORS.sapRed,
        boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightRed}`,
      },
    },
  },
  
  '& .MuiOutlinedInput-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    padding: SAP_SPACING.component.padding,
    lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
    
    '&::placeholder': {
      color: SAP_COLORS.sapGray4,
      opacity: 1,
      fontStyle: 'italic',
    },
    
    ...(loading && {
      color: SAP_COLORS.sapGray4,
      cursor: 'wait',
    }),
  },
  
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    
    '&.Mui-focused': {
      color: getFocusLabelColor(state),
      fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    },
    
    '&.Mui-error': {
      color: SAP_COLORS.sapRed,
    },
  },
  
  '& .MuiAutocomplete-popupIndicator': {
    color: SAP_COLORS.sapGray5,
    transition: SAP_TRANSITIONS.fast,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
      color: SAP_COLORS.sapBlue,
    },
  },
  
  '& .MuiAutocomplete-clearIndicator': {
    color: SAP_COLORS.sapGray5,
    transition: SAP_TRANSITIONS.fast,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.sapLightRed,
      color: SAP_COLORS.sapRed,
    },
  },
  
  '& .MuiAutocomplete-tag': {
    backgroundColor: SAP_COLORS.sapLightBlue,
    color: SAP_COLORS.sapBlue,
    border: `1px solid ${SAP_COLORS.sapBlue}`,
    borderRadius: SAP_SPACING.component.borderRadius,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    margin: SAP_SPACING.xs,
    
    '& .MuiChip-deleteIcon': {
      color: SAP_COLORS.sapBlue,
      '&:hover': {
        color: SAP_COLORS.sapRed,
      },
    },
  },
  
  ...customStyles,
}));

// Custom Popper component
const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiAutocomplete-paper': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: SAP_SPACING.component.borderRadius,
    border: `1px solid ${SAP_COLORS.sapGray3}`,
    boxShadow: SAP_SHADOWS.lg,
    marginTop: SAP_SPACING.xs,
    maxHeight: '300px',
    overflow: 'hidden',
    
    '& .MuiAutocomplete-listbox': {
      padding: 0,
      
      '& .MuiAutocomplete-option': {
        minHeight: '48px',
        padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
        borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
        transition: SAP_TRANSITIONS.fast,
        
        '&:hover': {
          backgroundColor: SAP_COLORS.hover,
        },
        
        '&.Mui-focused': {
          backgroundColor: SAP_COLORS.sapLightBlue,
          color: SAP_COLORS.sapBlue,
        },
        
        '&.Mui-selected': {
          backgroundColor: SAP_COLORS.sapBlue,
          color: SAP_COLORS.sapWhite,
          
          '&:hover': {
            backgroundColor: SAP_COLORS.sapDarkBlue,
          },
        },
        
        '&.Mui-disabled': {
          backgroundColor: SAP_COLORS.disabled,
          color: SAP_COLORS.sapGray4,
          cursor: 'not-allowed',
        },
      },
    },
  },
}));

// Group header component
const GroupHeader = styled(Box)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapGray1,
  padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
  borderBottom: `1px solid ${SAP_COLORS.sapGray3}`,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
  fontSize: SAP_TYPOGRAPHY.fontSize.sm,
  color: SAP_COLORS.sapGray6,
  textTransform: 'uppercase',
  letterSpacing: SAP_TYPOGRAPHY.letterSpacing.wide,
}));

// Utility functions for dynamic styling
function getBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapGray3;
  }
}

function getHoverBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

function getFocusBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

function getFocusShadowColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapLightRed;
    case 'success': return SAP_COLORS.sapLightGreen;
    case 'warning': return SAP_COLORS.sapLightOrange;
    default: return SAP_COLORS.sapLightBlue;
  }
}

function getFocusLabelColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

// Character count component
const CharacterCount = ({ current, max }: { current: number; max: number }) => (
  <Typography
    variant="caption"
    sx={{
      position: 'absolute',
      right: SAP_SPACING.sm,
      top: '50%',
      transform: 'translateY(-50%)',
      color: current > max * 0.9 ? SAP_COLORS.sapOrange : SAP_COLORS.sapGray5,
      fontSize: SAP_TYPOGRAPHY.fontSize.xs,
      fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
      pointerEvents: 'none',
    }}
  >
    {current}/{max}
  </Typography>
);

// Default option renderer
const DefaultOptionRenderer = (
  props: any,
  option: SAPInputOption,
  showDescription: boolean = false,
  showIcon: boolean = false,
  showCode: boolean = false,
  showStatus: boolean = false
) => (
  <ListItem {...props} sx={{ padding: 0 }}>
    {showIcon && (
      <ListItemIcon sx={{ minWidth: 40 }}>
        {option.icon || getDefaultIcon(option)}
      </ListItemIcon>
    )}
    <ListItemText
      primary={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              color: 'inherit',
            }}
          >
            {option.label}
          </Typography>
          {showCode && option.code && (
            <Chip
              label={option.code}
              size="small"
              sx={{
                fontSize: SAP_TYPOGRAPHY.fontSize.xs,
                height: '20px',
                backgroundColor: SAP_COLORS.sapGray2,
                color: SAP_COLORS.sapGray6,
              }}
            />
          )}
          {showStatus && option.status !== undefined && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getStatusColor(option.status),
              }}
            />
          )}
        </Box>
      }
      secondary={
        showDescription && option.description ? (
          <Typography
            variant="caption"
            sx={{
              color: SAP_COLORS.sapGray5,
              fontSize: SAP_TYPOGRAPHY.fontSize.xs,
              lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
            }}
          >
            {option.description}
          </Typography>
        ) : null
      }
    />
  </ListItem>
);

// Default icon getter
function getDefaultIcon(option: SAPInputOption): React.ReactNode {
  if (option.group === 'client') return <BusinessIcon />;
  if (option.group === 'job') return <WorkIcon />;
  if (option.group === 'user') return <PersonIcon />;
  return <SearchIcon />;
}

// Status color getter
function getStatusColor(status: number): string {
  switch (status) {
    case 1: return SAP_COLORS.sapGreen;
    case 2: return SAP_COLORS.sapOrange;
    case 3: return SAP_COLORS.sapRed;
    default: return SAP_COLORS.sapGray4;
  }
}

// Main component with forwardRef for proper ref handling
export const SAPInput = forwardRef<HTMLDivElement, SAPInputProps>(
  (
    {
      variant = 'outlined',
      size = 'md',
      state = 'default',
      fullWidth = true,
      loading = false,
      loadingText = 'Loading...',
      helperText,
      helperTextType = 'info',
      startIcon,
      endIcon,
      showCharacterCount = false,
      maxLength,
      validateOnBlur = true,
      validateOnChange = false,
      customStyles,
      onValidationChange,
      onStateChange,
      ariaLabel,
      ariaDescribedBy,
      value = '',
      onChange,
      onBlur,
      type = 'text',
      options = [],
      multiple = false,
      freeSolo = false,
      clearable = true,
      groupBy,
      groupLabelRenderer,
      filterOptions,
      highlightMatches = true,
      showOptionDescription = false,
      showOptionIcon = false,
      showOptionCode = false,
      showOptionStatus = false,
      onOptionSelect,
      onInputChange,
      noOptionsText = 'No options available',
      optionHeight = 48,
      maxOptions = 50,
      ...props
    },
    ref
  ) => {
    // Local state for autocomplete
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    // Memoized character count
    const characterCount = useMemo(() => {
      if (!showCharacterCount || !maxLength) return null;
      const current = typeof value === 'string' ? value.length : 0;
      return { current, max: maxLength };
    }, [showCharacterCount, maxLength, value]);

    // Memoized validation state
    const validationState = useMemo(() => {
      if (!maxLength || !showCharacterCount) return state;
      const current = typeof value === 'string' ? value.length : 0;
      if (current > maxLength) return 'error';
      if (current > maxLength * 0.9) return 'warning';
      return 'success';
    }, [state, maxLength, showCharacterCount, value]);

    // Enhanced accessibility props
    const accessibilityProps = {
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-busy': loading,
      'aria-invalid': validationState === 'error',
    };

    // Enhanced input props
    const inputProps = {
      ...props.InputProps,
      startAdornment: startIcon ? (
        <InputAdornment position="start">
          {startIcon}
        </InputAdornment>
      ) : props.InputProps?.startAdornment,
      endAdornment: (
        <>
          {loading && (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          )}
          {endIcon && (
            <InputAdornment position="end">
              {endIcon}
            </InputAdornment>
          )}
          {props.InputProps?.endAdornment}
        </>
      ),
    };

    // Enhanced helper text
    const finalHelperText = useMemo(() => {
      if (loading) return loadingText;
      if (helperText) return helperText;
      if (showCharacterCount && characterCount) {
        const { current, max } = characterCount;
        if (current > max) {
          return `Maximum ${max} characters allowed`;
        }
      }
      return '';
    }, [loading, loadingText, helperText, showCharacterCount, characterCount]);

    // Enhanced helper text type
    const finalHelperTextType = useMemo(() => {
      if (loading) return 'info';
      if (helperTextType) return helperTextType;
      if (showCharacterCount && characterCount) {
        const { current, max } = characterCount;
        if (current > max) return 'error';
        if (current > max * 0.9) return 'warning';
        return 'success';
      }
      return 'info';
    }, [loading, helperTextType, showCharacterCount, characterCount]);

    // Memoized filtered options for autocomplete
    const filteredOptions = useMemo(() => {
      if (!filterOptions) {
        return options.filter(option => !option.disabled).slice(0, maxOptions);
      }
      return filterOptions([...options], { inputValue }).slice(0, maxOptions);
    }, [options, inputValue, filterOptions, maxOptions]);

    // Enhanced filter options with highlighting
    const enhancedFilterOptions = useCallback((options: SAPInputOption[], params: any) => {
      const filtered = options.filter(option => !option.disabled);
      return filtered.filter(option =>
        option.label.toLowerCase().includes(params.inputValue.toLowerCase()) ||
        (option.code && option.code.toLowerCase().includes(params.inputValue.toLowerCase())) ||
        (option.description && option.description.toLowerCase().includes(params.inputValue.toLowerCase()))
      );
    }, []);

    // Enhanced option renderer
    const renderOption = useCallback((props: any, option: SAPInputOption) => {
      return DefaultOptionRenderer(
        props,
        option,
        showOptionDescription,
        showOptionIcon,
        showOptionCode,
        showOptionStatus
      );
    }, [showOptionDescription, showOptionIcon, showOptionCode, showOptionStatus]);

    // Enhanced group renderer
    const renderGroup = useCallback((params: any) => {
      return (
        <Box key={params.key}>
          <GroupHeader>
            {groupLabelRenderer ? groupLabelRenderer(params.group) : params.group}
          </GroupHeader>
          {params.children}
        </Box>
      );
    }, [groupLabelRenderer]);

    // Enhanced input change handler
    const handleInputChange = useCallback((event: React.SyntheticEvent, value: string, reason: any) => {
      setInputValue(value);
      onInputChange?.(value);
    }, [onInputChange]);

    // Enhanced change handler
    const handleChange = useCallback((event: React.SyntheticEvent, newValue: any) => {
      onChange?.(event as any);
      onOptionSelect?.(newValue);
    }, [onChange, onOptionSelect]);

    // Render autocomplete
    if (type === 'autocomplete') {
      return (
        <Box
          ref={ref}
          sx={{
            position: 'relative',
            width: fullWidth ? '100%' : 'auto',
          }}
        >
          {/** Cast to any to align MUI Autocomplete generics with our option type */}
          {/** eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <StyledAutocomplete as={undefined as any}
            variant={variant}
            state={validationState}
            fullWidth={fullWidth}
            loading={loading}
            helperTextType={finalHelperTextType}
            customStyles={customStyles}
            optionHeight={optionHeight}
            options={filteredOptions}
            value={value}
            onChange={handleChange as any}
            inputValue={inputValue}
            onInputChange={handleInputChange as any}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            filterOptions={enhancedFilterOptions as any}
            renderOption={renderOption as any}
            renderGroup={groupBy ? renderGroup : undefined}
            groupBy={groupBy as any}
            loadingText={loadingText}
            noOptionsText={noOptionsText}
            getOptionLabel={(option: any) => {
              if (typeof option === 'string') return option;
              return option.label || '';
            }}
            getOptionDisabled={(option: any) => option.disabled || false}
            isOptionEqualToValue={(option: any, value: any) => {
              if (typeof option === 'string' || typeof value === 'string') {
                return option === value;
              }
              return option.value === value.value;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={props.label as string}
                placeholder={props.placeholder as string}
                required={props.required}
                error={validationState === 'error'}
                helperText={finalHelperText}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: startIcon ? (
                    <InputAdornment position="start">
                      {startIcon}
                    </InputAdornment>
                  ) : params.InputProps?.startAdornment,
                  endAdornment: (
                    <>
                      {loading && (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      )}
                      {endIcon && (
                        <InputAdornment position="end">
                          {endIcon}
                        </InputAdornment>
                      )}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            componentsProps={{
              popper: {
                component: StyledPopper,
              },
            }}
            disabled={loading || props.disabled}
            {...accessibilityProps}
            {...(props as any)}
          />
        </Box>
      );
    }

    // Render text field
    return (
      <Box
        ref={ref}
        sx={{
          position: 'relative',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        <StyledTextField
          variant={variant}
          size={((size === 'xs' || size === 'sm') ? 'small' : 'medium') as any}
          state={validationState}
          fullWidth={fullWidth}
          loading={loading}
          helperTextType={finalHelperTextType}
          showCharacterCount={showCharacterCount}
          customStyles={customStyles}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          helperText={finalHelperText}
          InputProps={inputProps}
          disabled={loading || props.disabled}
          {...accessibilityProps}
          {...props}
          sx={{
            ...props.sx,
          }}
        />
        
        {/* Character count overlay */}
        {showCharacterCount && characterCount && (
          <CharacterCount
            current={characterCount.current}
            max={characterCount.max}
          />
        )}
      </Box>
    );
  }
);

// Display name for debugging
SAPInput.displayName = 'SAPInput';

// Export types for external use
export { inputVariants };
